import { useEffect, useState } from "react";
import { fetchProgramNewsRun, runStatusOf, type ProgramNewsRun } from "@/services/adminProgramNews";

export const DEFAULT_RUN_POLL_MS = 5000;

export interface RunPollerOptions {
  /** How often to ask the API for the run; 5 s by default, shorter in tests. */
  intervalMs?: number;
}

export interface RunPollerState {
  /** The latest copy of the run from the API; null until the first poll answers. */
  run: ProgramNewsRun | null;
  /** True once the run's status is no longer "running". Polling stops then. */
  done: boolean;
}

const IDLE: RunPollerState = { run: null, done: false };

/**
 * Polls one program-news run by id until it finishes. A transient fetch error keeps
 * polling; unmounting or clearing the id stops it. Null id means nothing to poll.
 */
export function useRunPoller(
  runId: string | null,
  { intervalMs = DEFAULT_RUN_POLL_MS }: RunPollerOptions = {}
): RunPollerState {
  const [state, setState] = useState<RunPollerState>(IDLE);

  useEffect(() => {
    setState(IDLE);
    if (!runId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      timer = setTimeout(tick, intervalMs);
    };

    const tick = async () => {
      let run: ProgramNewsRun | null = null;
      try {
        const response = await fetchProgramNewsRun(runId);
        run = response?.run ?? null;
      } catch {
        // Transient network or server error: keep polling.
      }
      if (cancelled) return;
      if (!run) {
        schedule();
        return;
      }
      const done = runStatusOf(run) !== "running";
      setState({ run, done });
      if (!done) schedule();
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, intervalMs]);

  return state;
}
